import { useCallback, useEffect, useState } from "react";
import useSmoothScroll from "./lib/useSmoothScroll";
import { ScrollTrigger } from "./lib/motion";

import Preloader, { PRELOADER_MAX_MS } from "./components/Preloader";
import Cursor from "./components/ui/Cursor";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Dune from "./components/Dune";
import Features from "./components/Features";
import Getaway from "./components/Getaway";
import Services from "./components/Services";
import Testimonials from "./components/Testimonials";
import Pricing from "./components/Pricing";
import CallToAction from "./components/CallToAction";
import Footer from "./components/Footer";

/**
 * The reference site ships Pricing and Blog markup but hides both wrappers with
 * `display:none`, so neither appears on the live page. We match the rendered
 * site by default; set this to true to surface the pricing table.
 */
const SHOW_PRICING = false;

export default function App() {
  const [ready, setReady] = useState(false);

  const handleLoaded = useCallback(() => setReady(true), []);

  useSmoothScroll();

  /**
   * `ready` gates the hero intro, and `.hero__arrow` starts at opacity 0 in CSS
   * until that timeline renders it — so a preloader that crashed or never
   * called back would leave the hero permanently incomplete. Backstop on the
   * wall clock (setTimeout, never rAF), just behind the curtain's own ceiling
   * so it can only fire if the preloader failed to report.
   */
  useEffect(() => {
    const id = setTimeout(handleLoaded, PRELOADER_MAX_MS + 300);
    return () => clearTimeout(id);
  }, [handleLoaded]);

  // Sections resize as fonts and images settle; keep triggers honest.
  useEffect(() => {
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", onLoad);
    const id = setTimeout(onLoad, 1200);
    return () => {
      window.removeEventListener("load", onLoad);
      clearTimeout(id);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("js-ready");
  }, []);

  return (
    <>
      <Preloader onDone={handleLoaded} />
      <Cursor />

      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <Header />

      <main id="main">
        <Hero ready={ready} />
        <Dune />
        <Features />
        <Getaway />
        <Services />
        <Testimonials />
        {SHOW_PRICING && <Pricing />}
        <CallToAction />
      </main>

      <Footer />
    </>
  );
}
